import { useCallback } from 'react';
import {
  useController,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
  PathValue,
} from 'react-hook-form';

import { Location } from '@/types/location';

import { LocationAssignmentSelect } from '../location-assignment-select';

interface LocationAssignmentSelectInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  rules?: RegisterOptions<TFieldValues, TName>;
  placeholder?: string;
  disabled?: boolean;
  excludeLocationIds?: string[];
}

export type LocationAssignmentSelectInputValue = Location | null;

export const LocationAssignmentSelectInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  rules,
  placeholder,
  disabled: disabledProp,
  excludeLocationIds,
}: LocationAssignmentSelectInputProps<TFieldValues, TName>) => {
  const form = useFormContext<TFieldValues>();

  // Use controller for form integration
  const {
    field: { value, onChange },
    fieldState: { error: fieldError },
    formState: { disabled: formDisabled },
  } = useController({
    name,
    control: form.control,
    rules,
    defaultValue: null as PathValue<TFieldValues, TName>,
  });

  const location = value;
  const disabled = disabledProp ?? formDisabled;

  const handleOnChange = useCallback(
    (location: Location | null) => {
      onChange(location);
    },
    [onChange],
  );

  const handleOnClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <LocationAssignmentSelect
      location={location}
      onChange={handleOnChange}
      onClear={handleOnClear}
      error={fieldError?.message}
      placeholder={placeholder}
      disabled={disabled}
      excludeLocationIds={excludeLocationIds}
    />
  );
};
